import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Axios instance that automatically injects the access token
const authAxios = axios.create({ baseURL: API_BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export interface ConfigScheme {
  key: string;
  label?: string;                         // Display name for UI (fallback to key if not set)
  schemaDefinition: Record<string, any>;  // The actual JSON schema
  schema?: Record<string, any>;           // Alias for backward compatibility
  description?: string;
  type: 'config' | 'collection';
  pinToSidebar?: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigData {
  key: string;
  config: Record<string, any>;
  schema?: Record<string, any>;
  isValid: boolean;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  validationErrors?: string[];
}

export interface CollectionItem {
  id: string;
  data: Record<string, any>;
  isValid: boolean;
  createdAt: string;
  updatedAt: string;
  validationErrors?: string[];
}

export interface CollectionResponse {
  items: CollectionItem[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  resultCount?: number;  // Old format support
  schemaKey?: string;    // Old format support
  query?: string;        // Old format support
  searchProp?: string;   // Old format support
}

export type AllowedRole = 'public' | 'admin' | 'manager' | 'user';
export type PermissionAction = 'create' | 'read' | 'update' | 'delete';

export interface SchemaPermissionConfig {
  schemaKey: string;
  create: AllowedRole[];
  read: AllowedRole[];
  update: AllowedRole[];
  delete: AllowedRole[];
  isDefault?: boolean;
}

class ConfigManagementService {
  /**
   * Create or update a configuration scheme
   */
  async createScheme(key: string, schema: Record<string, any>, description?: string, type: 'config' | 'collection' = 'config', pinToSidebar = false, label?: string) {
    try {
      const response = await authAxios.post(`/globals/scheme`, {
        key,
        schema,
        description,
        label,
        type,
        pinToSidebar,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get schema by key
   */
  async getSchemeByKey(key: string): Promise<ConfigScheme> {
    try {
      const response = await authAxios.get(`/globals/scheme/${key}`);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all schemes
   */
  async getAllSchemes(): Promise<ConfigScheme[]> {
    try {
      const response = await authAxios.get(`/globals/schemes`);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete scheme by key
   * Also deletes the corresponding dynamic collection(s)
   */
  async deleteScheme(key: string) {
    try {
      const response = await authAxios.delete(`/globals/scheme/${key}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new collection item
   */
  async createCollectionItem(schemaKey: string, data: Record<string, any>) {
    try {
      const response = await authAxios.post(`/globals/collection/${schemaKey}`, { data });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get collection items with pagination
   */
  async getCollectionItems(schemaKey: string, page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc'): Promise<CollectionResponse> {
    try {
      const response = await authAxios.get(`/globals/collection/${schemaKey}`, {
        params: { page, limit, sortBy, sortOrder },
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get available search fields from schema (only string-type fields)
   */
  getAvailableSearchFields(schemaDefinition: Record<string, any>): string[] {
    const properties = schemaDefinition?.properties || schemaDefinition?.items?.properties || {};
    return Object.keys(properties).filter(fieldName => {
      const xControlType = properties[fieldName]?.['x-control']?.type;
      const jsonType = properties[fieldName]?.type;
      return xControlType === 'string' || xControlType === 'text' || jsonType === 'string';
    });
  }

  /**
   * Search collection items with query string, sorting, and specific search fields
   */
  async searchCollectionItems(
    schemaKey: string,
    query: string,
    page = 1,
    limit = 10,
    sortBy = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    searchFields: string[] = []
  ): Promise<CollectionResponse> {
    try {
      const response = await authAxios.post(`/globals/collection/${schemaKey}/search`, {
        q: query || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
        ...(searchFields.length > 0 ? { searchFields } : {}),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a collection item
   * Requires schemaKey to identify which collection to update
   */
  async updateCollectionItem(id: string, schemaKey: string, data: Record<string, any>) {
    try {
      const response = await authAxios.put(`/globals/collection/${id}`, { schemaKey, data });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a collection item
   * Requires schemaKey to identify which collection to delete from
   */
  async deleteCollectionItem(id: string, schemaKey: string) {
    try {
      const response = await authAxios.delete(`/globals/collection/${id}`, { data: { schemaKey } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get configuration data by key
   */
  async getConfig(key: string): Promise<ConfigData> {
    try {
      const response = await authAxios.get(`/globals/config/${key}`);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update configuration data
   */
  async updateConfig(key: string, config: Record<string, any>) {
    try {
      const response = await authAxios.put(`/globals/config`, { key, config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Export schemas (+ optional data) as a ZIP file download
   * keys: [] means export all
   */
  async exportSchemas(keys: string[], includeData: boolean): Promise<void> {
    const response = await authAxios.post(
      `/globals/export`,
      { keys, includeData },
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().split('T')[0];
    a.download = `schemas-export-${date}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Import schemas (+ data) from a ZIP file
   * overwrite: replace existing schemas/data if key already exists
   */
  async importSchemas(
    file: File,
    overwrite: boolean
  ): Promise<{ results: Array<{ key: string; status: string; dataImported: boolean; error?: string }>; summary: { created: number; updated: number; skipped: number; total: number } }> {
    const form = new FormData();
    form.append('file', file);
    form.append('overwrite', String(overwrite));
    const response = await authAxios.post(`/globals/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { overwrite },
    });
    return response.data.data;
  }

  /**
   * Get permission config for a schema key
   */
  async getPermission(schemaKey: string): Promise<SchemaPermissionConfig> {
    try {
      const response = await authAxios.get(`/globals/permissions/${schemaKey}`);
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create or update permission config for a schema key
   */
  async upsertPermission(schemaKey: string, permission: Omit<SchemaPermissionConfig, 'schemaKey' | 'isDefault'>) {
    try {
      const response = await authAxios.put(`/globals/permissions/${schemaKey}`, permission);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message;
      return new Error(message);
    }
    return error;
  }
}

const configService = new ConfigManagementService();
export default configService;
